import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines, scrollToId } from "../lib/ui";

const HERO_BG =
  "https://images.unsplash.com/photo-1606767041004-6b918abe92be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden bg-sand pt-40">
      <motion.div
        style={{ y: bgY }}
        className="pointer-events-none absolute inset-0 z-0"
      >
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-[0.14] grayscale" />
        <div className="absolute inset-0 spotlight" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-16 bg-rust" />
          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-rust">
            [ For Research Only · Est. 2026 ]
          </span>
        </motion.div>

        <h1 className="font-display text-ink leading-[0.82] tracking-tighter">
          <MaskedLines
            lines={["Southern", "Fried", "Peptides"]}
            lineClassName="text-[19vw] md:text-[15vw] lg:text-[13rem]"
            delay={0.3}
          />
        </h1>

        <div className="mt-10 grid gap-10 md:grid-cols-[1fr_auto] md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="max-w-xl font-body text-lg leading-relaxed text-ink/80"
          >
            Research-grade peptides, without compromise. Third-party verified,
            purity-tested and COA-backed — the most-requested compounds, shipped discreetly.
          </motion.p>

          <motion.a
            href="#catalog"
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9 }}
            onClick={(e) => {
              e.preventDefault();
              scrollToId("catalog");
            }}
            className="group inline-flex w-fit items-center gap-3 border border-ink bg-transparent px-8 py-4 text-ink transition-colors duration-300 hover:bg-ink hover:text-cream"
          >
            <span className="font-mono text-xs uppercase tracking-[0.2em]">Browse the Catalog</span>
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" strokeWidth={1.5} />
          </motion.a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50 md:block md:right-12"
      >
        SOUTHERNFRIEDPEPTIDES.COM
      </motion.div>
    </section>
  );
};
